import React, { useCallback, useEffect } from 'react'

import { SocialData } from '../../@types'
import { useAuth } from '../../context/auth'
import { useSessionCart } from '../../context/cart'
import { useConfig } from '../../context/config'
import { lockSlot } from '../../lib/api/schedule/slot-lock'
import { socialLogin, SocialLoginAuthRequestPayload, Tokens } from '../../lib/auth'
import { cart } from '../../lib/cart'
import { HomeStackScreenProps } from '../../navigation/MainTabs/Home'
import { REDIRECTION_REASON_UNAUTHORIZED } from '../Welcome/redirect-reasons'

type Props = HomeStackScreenProps<'AccountConnectRedirect'>

export const AccountConnectRedirect: React.FC<Props> = ({ route, navigation }) => {
  const { config } = useConfig()
  const { saveTokens, socialAuth } = useAuth()
  const { sessionKey, product, addProductToCart } = useSessionCart()

  const goToCheckout = useCallback(() => {
    if (product?.schedule && product.clinicianId && sessionKey) {
      lockSlot(product.clinicianId, sessionKey, product.productId, product.schedule)
    }
    return navigation.reset({ index: 0, routes: [{ name: 'Checkout' }] })
  }, [navigation, product?.clinicianId, product?.productId, product?.schedule, sessionKey])

  const goToSelectProfile = useCallback(() => {
    if (product?.schedule && product.clinicianId && sessionKey) {
      lockSlot(product.clinicianId, sessionKey, product.productId, product.schedule)
    }
    navigation.reset({ index: 0, routes: [{ name: 'SelectProfile' }] })
  }, [navigation, product?.clinicianId, product?.productId, product?.schedule, sessionKey])

  const handleCartSession = useCallback(async () => {
    if (product) return true

    const cartProductFromMemory = await cart.getFromMemory()

    if (cartProductFromMemory?.productId && (cartProductFromMemory.symptoms?.length || cartProductFromMemory.summary)) {
      await addProductToCart(cartProductFromMemory, false)
      return true
    } else {
      return false
    }
  }, [product])

  const handleAuthenticatedUser = useCallback(
    async (data: SocialLoginAuthRequestPayload) => {
      const isCartSession = await handleCartSession()
      saveTokens(data as Tokens, false)

      if (!isCartSession) return navigation.reset({ index: 0, routes: [{ name: 'HowCanWeHelp' }] })

      if (config.canPatientAddDependents) {
        return goToSelectProfile()
      }

      return goToCheckout()
    },
    [config.canPatientAddDependents, goToCheckout, handleCartSession, navigation, saveTokens]
  )

  const handleNewUser = useCallback(
    async (data: SocialLoginAuthRequestPayload) => {
      const socialAccountData = data?.socialAccountData as unknown as SocialData
      await socialAuth(socialAccountData, data?.accessToken)

      return navigation.navigate('Auth', {
        screen: 'CreateAccountProfile',
        params: { socialAccountData, isPhoneVerified: false },
      })
    },
    [navigation, socialAuth]
  )

  const handleRegisteredUser = useCallback(
    async (data?: SocialLoginAuthRequestPayload) => {
      return navigation.navigate('Auth', {
        screen: 'SignInMFA',
        params: {
          token: data?.accessToken as string,
          maskedPhone: data?.maskedPhone,
        },
      })
    },
    [navigation]
  )

  useEffect(() => {
    async function setCart() {
      if (product) {
        return goToCheckout()
      } else {
        const cartProductFromMemory = await cart.getFromMemory()

        if (
          cartProductFromMemory?.productId &&
          (cartProductFromMemory.symptoms?.length || cartProductFromMemory.summary)
        ) {
          addProductToCart(cartProductFromMemory, false)
          return goToCheckout()
        }
      }
    }

    setCart()
  }, [])

  useEffect(() => {
    const makeRequest = async (provider: string, token: string) => {
      try {
        const { data } = await socialLogin.auth(provider, token)

        if (!data) {
          return navigation.navigate('Welcome', { reason: REDIRECTION_REASON_UNAUTHORIZED })
        }
        if (data.refreshToken) {
          return await handleAuthenticatedUser(data)
        }
        if (data.isNewUser) {
          return await handleNewUser(data)
        } else {
          return handleRegisteredUser(data)
        }
      } catch (_) {
        return navigation.goBack()
      }
    }

    makeRequest(route.params.provider, route.params.access_token)
  }, [
    handleAuthenticatedUser,
    handleNewUser,
    handleRegisteredUser,
    navigation,
    route.params.access_token,
    route.params.provider,
  ])

  return null
}

export default AccountConnectRedirect
